import React, { useCallback, useEffect, useState, useMemo } from "react";
import NonParticipantTable from "../../library/NonParticipantTable/NonParticipantTable";
import { ToastType } from "../../ToastManager/constants";
import { sendNotification } from "../../ToastManager/ToastManager";
import dynamic from "@redux-dynostore/react-redux";
import subspaced from "@redux-dynostore/react-redux-subspace";
import { attachReducer } from "@redux-dynostore/redux-subspace";
import { connect } from "react-redux";
import {
  mapDispatchToProps,
  mapStateToProps,
  nonParticipantsReducer,
} from "./NonParticipantTable.store";
import CustomModal from "../../ui-library/CustomModal/CustomModal";
import SmartAddNonParticipant from "../AddNonParticipant/AddNonParticipant";
import SmartAddSite from "../AddSite/AddSite";
import SmartAddUserAccessManagement from "../../smart/AddUserAccessManagement/AddUserAccessManagement";
import { mapStateToPropsManagement } from "../../../modules/profileManagement";
import { RegistrationType } from "../../library/AddUserAccessManagement/AddUserAccessManagement.constants";
import { profiles } from "../../../modules/constants";
import PrimaryButton from "../../ui-library/Buttons/PrimaryButton/PrimaryButton";
import ConfirmRequest from "../../library/ConfirmRequest/ConfirmRequest";
import { useTranslation } from "react-i18next";

export const SmartNonParticipantTable = (props) => {
  const {
    studyId,
    searchedParams,
    totalItems,
    nonParticipants,
    fetchNonParticipants,
    resendInvite,
    resetPassword,
    sites,
    fetchSites,
    unlockUser,
    blockUnblockUser,
    fetchAvailableRoles,
    roles,
    studyCode,
    canBlockUser,
    canUnlockUser,
    canEditUser,
    accountUsername,
    canAddNonParticipant,
    canAddSite,
    canResendInviteNonParticipant,
    canResetPasswordNonParticipant,
    resetState,
    loading,
  } = props;

  const { t } = useTranslation();

  const [blockConfirmRequest, setBlockConfirmRequest] = useState(null);
  const [showAddNonParticipantModal, setShowAddNonParticipantModal] =
    useState(false);
  const [showAddSiteModal, setShowAddSiteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [availableRoles, setAvailableRoles] = useState([]);

  const sitesFormatted = useMemo(
    () => sites?.map((site) => ({ id: site.key, value: site.name })),
    [sites]
  );

  useEffect(() => {
    return () => resetState();
  }, [resetState]);

  const getData = useCallback(
    (params, onSuccess = () => {}) => {
      fetchNonParticipants(params, studyId)
        .then(() => {
          onSuccess();
        })
        .catch((error) => {
          sendNotification(error, ToastType.ERROR);
        });
    },
    [fetchNonParticipants, studyId]
  );

  const unlockAccount = (user) => {
    unlockUser(user.userName)
      .then((message) => {
        sendNotification(message, ToastType.SUCCESS);
        getData(searchedParams);
      })
      .catch((error) => {
        sendNotification(error, ToastType.ERROR);
      });
  };

  const handleResendInvite = (user) => {
    resendInvite(user.userName)
      .then((message) => {
        sendNotification(message, ToastType.SUCCESS);
        getData(searchedParams);
      })
      .catch((error) => {
        sendNotification(error, ToastType.ERROR);
      });
  };

  const handleResetPassword = (user) => {
    resetPassword(user.userName)
      .then((message) => {
        sendNotification(message, ToastType.SUCCESS);
        getData(searchedParams);
      })
      .catch((error) => {
        sendNotification(error, ToastType.ERROR);
      });
  };

  const unblockAccount = (user) => {
    blockUnblockUser(user.userName, false)
      .then((message) => {
        sendNotification(message, ToastType.SUCCESS);
        getData(searchedParams);
      })
      .catch((error) => {
        sendNotification(error, ToastType.ERROR);
      });
  };

  const blockAccount = (user) => {
    blockUnblockUser(user.userName, true)
      .then((message) => {
        sendNotification(message, ToastType.SUCCESS);
        getData(searchedParams);
      })
      .catch((error) => {
        sendNotification(error, ToastType.ERROR);
      });
  };

  const accessManagement = (user) => {
    setSelectedUser(user);
  };

  const closeAddNonParticipantModal = () => {
    setShowAddNonParticipantModal(false);

    getData(searchedParams);
  };

  const addNonParticipant = () => {
    fetchSites(studyId);
    if (!sites || sites.length === 0) {
      sendNotification(
        t("appText.nonParticipants.modal.add.site.unavailable"),
        ToastType.ERROR
      );
    } else {
      setShowAddNonParticipantModal(true);
    }
  };

  const addNewSite = () => {
    setShowAddSiteModal(true);
  };

  const closeAddNewSiteModal = () => {
    setShowAddSiteModal(false);
  };

  const onCloseAddUserAccessManagement = () => {
    setSelectedUser(null);

    getData(searchedParams);
  };

  const confirmBlockRequestHandler = () => {
    blockAccount(blockConfirmRequest);

    setBlockConfirmRequest(null);
  };

  useEffect(() => {
    fetchAvailableRoles();
  }, [fetchAvailableRoles]);

  useEffect(() => {
    if (totalItems === null) {
      getData(searchedParams);
    }
  }, [getData, searchedParams, totalItems]);

  useEffect(() => {
    fetchSites(studyId);
  }, [fetchSites, studyId]);

  useEffect(() => {
    setAvailableRoles(
      roles?.roles?.filter(
        (roleName) =>
          ![profiles.itAdmin, profiles.studyAdmin].includes(roleName)
      ) ?? []
    );
  }, [roles]);

  return (
    <>
      <CustomModal
        show={showAddNonParticipantModal}
        title={t("appText.nonParticipants.modal.add.header")}
        onClose={() => setShowAddNonParticipantModal(false)}
      >
        <SmartAddNonParticipant
          studyId={studyId}
          sites={sitesFormatted}
          roles={availableRoles}
          onClose={closeAddNonParticipantModal}
        />
      </CustomModal>

      <CustomModal
        title={`${t("appText.nonParticipants.addSite.addNew")} ${studyCode} ${t(
          "appText.nonParticipants.addSite.site"
        )}`}
        onClose={() => setShowAddSiteModal(false)}
        size="sm"
        show={showAddSiteModal}
      >
        <SmartAddSite
          studyId={studyId}
          onClose={closeAddNewSiteModal}
          SiteButton={PrimaryButton}
        />
      </CustomModal>

      <CustomModal
        title="User Access"
        onClose={() => setSelectedUser(null)}
        show={selectedUser !== null}
        titleSeparatorBg={props.theme?.greenGradient}
      >
        <SmartAddUserAccessManagement
          onClose={onCloseAddUserAccessManagement}
          registrationType={RegistrationType.Admin}
          selectedUser={selectedUser}
          roles={availableRoles}
          mainColor="primary"
          sites={sitesFormatted}
        />
      </CustomModal>

      <CustomModal
        title="Are you sure?"
        onClose={() => setBlockConfirmRequest(false)}
        show={!!blockConfirmRequest}
      >
        <ConfirmRequest
          message="Clicking Confirm will block user from being able to login to Rarify."
          onConfirm={confirmBlockRequestHandler}
          onClose={() => setBlockConfirmRequest(false)}
        />
      </CustomModal>

      <NonParticipantTable
        {...{ getData }}
        {...{ searchedParams }}
        totalitems={totalItems}
        loading={loading}
        {...{ nonParticipants }}
        {...{ unlockAccount }}
        blockAccount={setBlockConfirmRequest}
        {...{ unblockAccount }}
        {...{ addNewSite }}
        {...{ handleResendInvite }}
        {...{ handleResetPassword }}
        {...{ addNonParticipant }}
        {...{ accessManagement }}
        {...{ canBlockUser }}
        {...{ canUnlockUser }}
        {...{ canEditUser }}
        sites={sitesFormatted}
        {...{ accountUsername }}
        {...{ canAddNonParticipant }}
        {...{ canAddSite }}
        {...{ canResendInviteNonParticipant }}
        {...{ canResetPasswordNonParticipant }}
      />
    </>
  );
};

const mapToStateToProps = (state) => {
  return {
    ...mapStateToPropsManagement(state),
    ...mapStateToProps(state),
  };
};

export default dynamic(
  "nonParticipantsReducer",
  subspaced({
    // We map extra state because we might need to acess other stores for data, subspace is self contained.
    mapExtraState: (state, rootState) => ({
      loginReducer: state.loginReducer,
      profileManagement: rootState.profileManagement,
    }),
  }),
  attachReducer(nonParticipantsReducer)
)(connect(mapToStateToProps, mapDispatchToProps)(SmartNonParticipantTable));
